import { EchecApi } from '../../partage/api/client';
import {
  BANDEAU_FINIRAIT_APRES_MINUIT,
  BANDEAU_JOUR_INVALIDE,
  BANDEAU_PAS_DE_CONNEXION,
  BANDEAU_RIEN_N_A_CHANGE,
  FINIRAIT_APRES_MINUIT,
  heureQuiChevauche,
  JOUR_PASSE,
  JOURNEE_CHANGEE_AILLEURS,
  PAS_DE_CONNEXION_RIEN_D_ENREGISTRE,
  pasPossibleCeJour,
  prevueUnAutreJour,
  RIEN_N_EST_ENREGISTRE,
  RIEN_QUI_TIENNE,
} from '../../partage/vocabulaire';
import { heureLisible, jourEnToutesLettres } from './datesDeLAgenda';

/**
 * Les refus d'une journée, en mots, écrits une seule fois pour l'ordinateur (« Préparer ma
 * journée ») et le téléphone (chantier `nettoyage-journee`, ticket 03), et pour les bandeaux de
 * la veille et du lendemain, avec leurs mots à eux (chantier `nettoyage-mots`, ticket 05). Sans
 * rendu : les écrans, la capture et le + l'importent d'ici. Un refus n'est pas une panne : jamais
 * de rouge.
 */

/**
 * Où le refus se dit : `journee` pour une journée qu'on compose, `bandeau` pour les bandeaux de la
 * veille et du lendemain, qui gardent leurs phrases (« rien n'a changé »).
 */
export type ContexteDuRefus = 'journee' | 'bandeau';

/** Une tâche nommée dans les détails d'un refus du serveur. */
interface TacheDuRefus {
  titre?: unknown;
  jour?: unknown;
}

function titresDe(details: unknown): TacheDuRefus[] {
  const taches = (details as { taches?: unknown } | undefined)?.taches;
  return Array.isArray(taches) ? (taches as TacheDuRefus[]).filter((t) => typeof t?.titre === 'string') : [];
}

/** Un refus d'un bandeau : un jour qui ne tient pas, un jour refusé, pas de connexion ; le reste, rien n'a changé. */
function refusDuBandeau(code: string | null): string {
  switch (code) {
    case 'hors_ligne':
      return BANDEAU_PAS_DE_CONNEXION;
    case 'ne_tient_pas':
      return BANDEAU_FINIRAIT_APRES_MINUIT;
    case 'jour_invalide':
      return BANDEAU_JOUR_INVALIDE;
  }
  return BANDEAU_RIEN_N_A_CHANGE;
}

/**
 * Un refus du serveur, en mots simples : ↻ sans rien qui tienne, une heure fixée qui tombe sur autre
 * chose, et les refus de « C'est ma journée » et du rangement (`telephone-serveur`, tickets 03 et
 * 05), chacun avec la tâche nommée, et une journée changée ailleurs depuis qu'on l'a lue
 * (chantier nettoyage-regles, ticket 04). Le reste : rien n'est enregistré. Dans un bandeau, ses phrases.
 */
export function refusEnMots(code: string | null, details: unknown, contexte: ContexteDuRefus = 'journee'): string {
  if (contexte === 'bandeau') return refusDuBandeau(code);
  switch (code) {
    case 'rien_qui_tienne':
      return RIEN_QUI_TIENNE;
    case 'ne_tient_pas':
      return FINIRAIT_APRES_MINUIT;
    case 'jour_passe':
      return JOUR_PASSE;
    case 'hors_ligne':
      return PAS_DE_CONNEXION_RIEN_D_ENREGISTRE;
    case 'journee_changee':
      return JOURNEE_CHANGEE_AILLEURS;
    case 'heure_fixee_chevauche': {
      const d = details as { tache?: { titre: string; heure: string }; avec?: { titre: string; heure: string } } | undefined;
      if (d?.tache && d.avec) return heureQuiChevauche(d.tache.titre, heureLisible(d.tache.heure), d.avec.titre, heureLisible(d.avec.heure));
      break;
    }
    case 'pas_possible_ce_jour': {
      const taches = titresDe(details);
      if (taches.length > 0) return pasPossibleCeJour(taches.map((t) => t.titre as string));
      break;
    }
    case 'prevue_un_autre_jour': {
      const [t] = titresDe(details);
      if (t && typeof t.jour === 'string') return prevueUnAutreJour(t.titre as string, jourEnToutesLettres(t.jour));
      break;
    }
  }
  return RIEN_N_EST_ENREGISTRE;
}

/** Un échec de l'API, en mots simples. */
export function enMots(e: unknown, contexte: ContexteDuRefus = 'journee'): string {
  return e instanceof EchecApi ? refusEnMots(e.code, e.details, contexte) : refusEnMots(null, undefined, contexte);
}

/**
 * La phrase sous la journée : celle du dernier geste, sinon le refus du rangement automatique.
 * « C'est ma journée » grisé parce que le serveur a refusé de ranger a donc toujours sa phrase ;
 * elle part d'elle-même quand le rangement suivant passe.
 */
export function messageDuJour(message: string | null, refusDuRangement: unknown): string | null {
  if (message) return message;
  return refusDuRangement ? enMots(refusDuRangement) : null;
}
